import { Row } from '@components/Layout/Row';
import { I18n } from '@components/Trans';
import { createStyled } from '@lib/style/createStyled';
import { anchor } from '@lib/style/mixins/anchor';
import { prop } from '@lib/style/theme';
import { unitStrict } from '@lib/style/unit';
import Button from '@material-ui/core/Button';
import { Image, ImageStyle } from '@packages/image';
import { Lightbox } from '@packages/lightbox';
import { has, take } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { ImageProps } from './image';
import { ImageObject } from './types';

interface ImagesProps {
    list: string[] | ImageObject[] | File[];
    colWidth?: string | number;
    maxItems?: number;
    lightbox?: boolean;
    aspectRatio?: number;
    renderElement?: (data: {
        props: any;
        key: string;
        classes: {
            holder: string;
            inner: string;
        };
    }) => any;
    imageProps?: ImageProps;
}

function mapItem(item) {
    let item$ = {};

    if (item && typeof item === 'object') {
        const { isProps, ...rest } = item;
        const isFile = item instanceof File;
        const isImage = has(item, 'id');

        item$ = isProps || (!isFile && isImage) ? rest : { source: item };
    } else {
        item$ = { source: item };
    }

    return item$;
}

// eslint-disable-next-line react/display-name
const mapImage = (render, imageProps) => (item, idx) => {
    const item$ = mapItem(item);
    const props$ = {
        ...(imageProps || {}),
        ...item$,
    };

    const key = `image_${idx}`;
    const classes = {
        holder: 'image__holder',
        inner: 'image__inner',
    };

    if (render && typeof render === 'function') {
        return render({
            props: props$,
            key,
            classes,
            Image,
        });
    }

    return (
        <Button className={classes.holder} key={key}>
            <div className={classes.inner}>
                <Image {...props$} />
            </div>
        </Button>
    );
};

export const Images = (props: ImagesProps) => {
    const { list, lightbox, imageProps, renderElement, maxItems, ...rest } = props;

    if (!list) {
        return null;
    }

    const diff = list.length - (maxItems ?? 0);
    const items$ = take(list as any, maxItems);

    return (
        <Style {...rest}>
            <Lightbox images={list}>
                {items$.map(mapImage(renderElement, imageProps))}
                {diff > 0 ? (
                    <div className={'image__holder view-more'}>
                        <Button className="image__inner">
                            <I18n
                                className={'text'}
                                variant={'subtitle2'}
                                color={'textSecondary'}
                                props={{ count: diff }}
                            >
                                +:count More
                            </I18n>
                        </Button>
                    </div>
                ) : null}
            </Lightbox>
        </Style>
    );
};

const colWidth = prop('colWidth', '20%', unitStrict);

const Style = styled(createStyled(Row, [anchor as any, 'colWidth', 'aspectRatio']))`
    & {
        align-content: flex-start;
        align-items: flex-start;
        justify-content: flex-start;
        flex-wrap: wrap;
        .image__holder {
            display: inline-flex;
            flex: 1 0 ${colWidth};
            max-width: ${colWidth};
            width: ${colWidth};
            margin: 8px;
            padding: 0;
            &:not(.view-more) {
                border: 1px solid #bfbfbf;
            }
            &.view-more {
                .image__inner {
                    background-color: #f5f5f5;
                }
            }
            .image__inner {
                display: block;
                width: 100%;
                padding: 0 0 ${prop('aspectRatio', 1, (ratio) => `${100 * ratio}%`)} 0;
                position: relative;
                overflow: hidden;
                border-radius: 4px;
                * {
                    text-transform: capitalize;
                }
                .text {
                    display: inline-flex;
                    align-items: center;
                    align-content: center;
                    justify-content: center;
                    line-height: 1;
                }
                .text,
                ${ImageStyle} {
                    width: 100%;
                    height: 100%;
                    transform: translate3d(0, 0, 0) scale(1, 1);
                    will-change: transform;
                    transition: transform 200ms;
                }

                .text,
                ${ImageStyle}, &:after {
                    ${anchor()}
                }

                &:after {
                    content: '';
                    display: block;
                    opacity: 0;
                    pointer-events: none;
                    z-index: 3;
                    background-color: #222;
                    position: absolute;
                    will-change: opacity;
                    transition: opacity 200ms;
                }

                &:hover {
                    &:after {
                        opacity: 0.1;
                    }
                    .text,
                    ${ImageStyle} {
                        transform: translate3d(0, 0, 0) scale(1.1, 1.1);
                    }
                }
            }
        }
    }
`;
